/**
 * `storeBuyers.*` — the Store Admin buyer directory (Commerce, M3).
 *
 * A **buyer** is a storefront account: a phone number that signed in with an
 * OTP and registered a business name. That is not the same thing as a
 * **customer**, the ERP-side record an order and its invoice hang off.
 * Registration mints the skeleton customer, so `customerId` is set from the
 * start — it never means "has ordered".
 *
 * `list` and `counts` return only buyers who have **placed an order** (any
 * status, drafts included). Signing up does not put someone on this screen; the
 * seller's question is "who buys from me". Those accounts are unaffected — they
 * can still sign in, and they appear here the moment they order.
 *
 * Read-only by design. Buyers create and own these accounts from the
 * storefront; a seller editing one from the back office would desync the
 * details the buyer signs in and checks out with. Suspension — the one write a
 * seller plausibly needs — is deliberately **not** here: it locks a buyer out
 * of a store they can see, so it wants its own confirmation, audit reason and
 * spec, not a status dropdown smuggled into a list.
 *
 * **What is not on this row.** The module placeholder promised tier, rep and
 * credit limit. Tier and rep have no column anywhere in the schema, and
 * outstanding needs the ledger. Rendering an empty "Tier" column would be a
 * dead affordance, so the row carries only what the store actually knows.
 *
 * @see packages/domain/src/commerce/store/list-buyers.ts
 */
import { z } from "zod";
import { uuid, paged, moneyPaise } from "../_common.js";

/** `suspended` blocks sign-in and checkout; nothing here sets it yet. */
export const buyerStatus = z.enum(["active", "suspended"]);
export type BuyerStatus = z.infer<typeof buyerStatus>;

export const buyerRow = z.object({
  buyerId: uuid,
  /** The person who signed up. */
  name: z.string(),
  /** The business they buy for — the column an operator scans. */
  businessName: z.string(),
  phone: z.string(),
  /** Null when they registered without one — they get a B2C invoice. */
  gstin: z.string().nullable(),
  status: buyerStatus,
  /** The skeleton customer minted at registration — set for every buyer. */
  customerId: uuid.nullable(),
  /**
   * Orders that became a commitment — confirmed, approved or fulfilled. Drafts
   * and rejections are excluded, and `orderValuePaise` covers exactly the same
   * set, so the two figures can never describe different orders.
   *
   * Zero is a legitimate value on a listed buyer: `list` returns everyone who has
   * *placed* an order, and a draft awaiting confirmation is not yet a commitment.
   */
  orderCount: z.number().int().nonnegative(),
  orderValuePaise: moneyPaise,
  /** ISO. Null when they have never placed a counted order. */
  lastOrderAt: z.string().nullable(),
  /** ISO — when the account was created. */
  joinedAt: z.string(),
});
export type BuyerRow = z.infer<typeof buyerRow>;

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  /** Free text over business name, contact name and phone. */
  q: z.string().optional(),
  /** Omitted = every status (the "All" tab). */
  status: buyerStatus.optional(),
});
export const listOutput = paged(buyerRow);

export const countsOutput = z.object({
  all: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  suspended: z.number().int().nonnegative(),
});


// ── detail (the buyer's own dashboard) ────────────────────────────

export const getInput = z.object({ buyerId: uuid });

/** One order in the buyer's history — every status, not just commitments. */
export const buyerOrderRow = z.object({
  orderId: uuid,
  /** Empty while the order is still an unnumbered draft. */
  docNumber: z.string(),
  status: z.enum(["draft", "confirmed", "approved", "fulfilled", "rejected"]),
  kind: z.enum(["at_once", "prebook"]),
  createdAt: z.string(),
  qty: z.number().int().nonnegative(),
  totalPaise: moneyPaise,
});

export const buyerQuoteRow = z.object({
  quoteId: uuid,
  docNumber: z.string(),
  status: z.enum(["draft", "sent", "converted", "expired"]),
  /** Derived from status + expiry, the same fold the quote book uses. */
  phase: z.enum(["draft", "pending", "expiring", "expired", "converted"]),
  createdAt: z.string(),
  expiresOn: z.string().nullable(),
  totalPaise: moneyPaise,
  convertedOrderId: uuid.nullable(),
});

export const getOutput = buyerRow.extend({
  /**
   * Mean value of a committed order. Null rather than 0 when there are none —
   * "no orders yet" and "orders averaging nothing" are different facts.
   */
  avgOrderValuePaise: moneyPaise.nullable(),
  /** Quotes still awaiting the buyer: pending or expiring. */
  openQuoteCount: z.number().int().nonnegative(),
  /**
   * Full history, newest first, capped server-side. Longer than `orderCount`
   * whenever the buyer has drafts or rejections — the count is commitments.
   */
  orders: z.array(buyerOrderRow),
  quotes: z.array(buyerQuoteRow),
});

export type ListBuyersInput = z.infer<typeof listInput>;
export type BuyerDetail = z.infer<typeof getOutput>;
